import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import axios from "axios";

export const usePhotoCarousel = ({
  size = "small",
  numImages = 10,
  autoPlay = false,
  autoPlayInterval = 5000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentLargeImageUrl, setCurrentLargeImageUrl] = useState(null);
  const auth = getAuth();

  // Auto-play effect
  useEffect(() => {
    let intervalId;

    if (autoPlay && imageUrls.length > 1 && !modalOpen) {
      console.log(
        `PhotoCarousel: Setting up auto-play with interval ${autoPlayInterval}ms`
      );
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex < imageUrls.length - 1 ? prevIndex + 1 : 0
        );
      }, autoPlayInterval);
    }

    return () => {
      if (intervalId) {
        console.log("PhotoCarousel: Clearing auto-play interval");
        clearInterval(intervalId);
      }
    };
  }, [autoPlay, imageUrls.length, autoPlayInterval, modalOpen]);

  // Fetch images function
  const fetchImages = useCallback(async () => {
    console.log(
      `PhotoCarousel: fetchImages called. numImages: ${numImages}, size: ${size}`
    );
    setLoading(true);
    setError(null);

    const endpoint = `/api/media/random-small-image?count=${numImages}&requestedSize=${size}`;
    console.log("PhotoCarousel: Fetching from endpoint:", endpoint);

    try {
      let headers = {};
      const currentUser = auth.currentUser;

      if (currentUser) {
        console.log("PhotoCarousel: User is logged in. Fetching ID token.");
        const idToken = await currentUser.getIdToken();
        headers.Authorization = `Bearer ${idToken}`;
        console.log("PhotoCarousel: ID token fetched.");
      } else {
        console.log("PhotoCarousel: User is not logged in.");
      }

      console.log("PhotoCarousel: Making API request with headers:", headers);
      const response = await axios.get(endpoint, { headers });

      console.log("PhotoCarousel: API response status:", response.status);
      console.log("PhotoCarousel: API response data:", response.data);

      const imagesData = response.data;
      console.log(
        `PhotoCarousel: Received data for ${imagesData.length} images.`
      );

      const urls = imagesData.map((item, index) => {
        if (!item?.mediaUrl) {
          console.warn(
            `PhotoCarousel: Item ${index} has no mediaUrl. Using fallback.`
          );
          return "/missing.jpg";
        }

        if (item.mediaUrl[size]) {
          console.log(
            `PhotoCarousel: Item ${index} - Using requested size '${size}': ${item.mediaUrl[size]}`
          );
          return item.mediaUrl[size];
        }

        const availableSizes = Object.keys(item.mediaUrl);
        if (availableSizes.length > 0) {
          const bestAvailableSize = availableSizes[availableSizes.length - 1];
          console.warn(
            `PhotoCarousel: Item ${index} - Requested size '${size}' not found. Using best available '${bestAvailableSize}': ${item.mediaUrl[bestAvailableSize]}`
          );
          return item.mediaUrl[bestAvailableSize];
        }
        console.warn(
          `PhotoCarousel: Item ${index} - No available sizes found. Using fallback.`
        );
        return "/missing.jpg";
      });

      setImageUrls(urls);
      console.log("PhotoCarousel: Images processed and URLs set:", urls);
    } catch (e) {
      console.error("PhotoCarousel: Error fetching random images:", e);
      if (e.response) {
        console.error(
          "PhotoCarousel: Axios error response data:",
          e.response.data
        );
      }
      setError(e.message || "Failed to fetch images");
      const fallbackUrls = Array(numImages).fill("/missing.jpg");
      setImageUrls(fallbackUrls);
      console.log("PhotoCarousel: Set fallback URLs due to error.");
    } finally {
      setLoading(false);
      console.log("PhotoCarousel: fetchImages finished.");
    }
  }, [numImages, size, auth]);

  // Initial fetch and auth listener
  useEffect(() => {
    console.log(
      "PhotoCarousel: Component mounted or fetchImages/auth changed. Initial fetch starting."
    );
    fetchImages();

    console.log("PhotoCarousel: Setting up auth state change listener.");
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log(
          "PhotoCarousel: Auth state changed - user is logged in:",
          user.uid
        );
      } else {
        console.log("PhotoCarousel: Auth state changed - user is logged out.");
      }
      console.log(
        "PhotoCarousel: Re-fetching images due to auth state change."
      );
      fetchImages();
    });

    return () => {
      console.log(
        "PhotoCarousel: Component unmounting. Cleaning up auth listener."
      );
      unsubscribe();
    };
  }, [fetchImages, auth]);

  // Navigation functions
  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;
    console.log(
      `PhotoCarousel: goToPrevious clicked. Current index: ${currentIndex}, New index: ${newIndex}`
    );
    setCurrentIndex(newIndex);
  }, [currentIndex, imageUrls.length]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;
    console.log(
      `PhotoCarousel: goToNext clicked. Current index: ${currentIndex}, New index: ${newIndex}`
    );
    setCurrentIndex(newIndex);
  }, [currentIndex, imageUrls.length]);

  // Modal functions
  const handleImageClick = useCallback(() => {
    if (imageUrls[currentIndex]) {
      const clickedImageUrl = imageUrls[currentIndex];
      console.log(
        `PhotoCarousel: handleImageClick. Image URL: ${clickedImageUrl}, Index: ${currentIndex}`
      );
      setCurrentLargeImageUrl(clickedImageUrl);
      setModalOpen(true);
    } else {
      console.warn(
        "PhotoCarousel: handleImageClick - No image URL at current index:",
        currentIndex
      );
    }
  }, [imageUrls, currentIndex]);

  const handleModalClose = useCallback(() => {
    console.log("PhotoCarousel: handleModalClose called.");
    setModalOpen(false);
    setCurrentLargeImageUrl(null);
  }, []);

  const handleModalPrevious = useCallback(() => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;
    const newLargeImageUrl = imageUrls[newIndex];
    console.log(
      `PhotoCarousel: handleModalPrevious. New index: ${newIndex}, New large image URL: ${newLargeImageUrl}`
    );
    setCurrentIndex(newIndex);
    setCurrentLargeImageUrl(newLargeImageUrl);
  }, [currentIndex, imageUrls]);

  const handleModalNext = useCallback(() => {
    const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;
    const newLargeImageUrl = imageUrls[newIndex];
    console.log(
      `PhotoCarousel: handleModalNext. New index: ${newIndex}, New large image URL: ${newLargeImageUrl}`
    );
    setCurrentIndex(newIndex);
    setCurrentLargeImageUrl(newLargeImageUrl);
  }, [currentIndex, imageUrls]);

  return {
    // State
    currentIndex,
    imageUrls,
    loading,
    error,
    modalOpen,
    currentLargeImageUrl,

    // Actions
    goToPrevious,
    goToNext,
    handleImageClick,
    handleModalClose,
    handleModalPrevious,
    handleModalNext,

    // Computed
    hasImages: imageUrls.length > 0,
    canNavigate: imageUrls.length > 1,
    currentImageUrl: imageUrls[currentIndex],
  };
};
